import { Box, Button, Typography, styled } from "@mui/material";
import type { ElementType } from "react";

export const TOKENS = {
  PRIMARY_BLUE: "#20274a",
  LIGHT_BLUE: "#4a5568",
  ACCENT_BLUE: "#3182ce",
  ACCENT_PURPLE: "#805ad5",
  SUCCESS_GREEN: "#48bb78",
  WARNING_ORANGE: "#ed8936",
  DANGER_RED: "#e53e3e",
  GRAY_100: "#f7fafc",
  GRAY_200: "#edf2f7",
  GRAY_300: "#e2e8f0",
  GRAY_400: "#cbd5e0",
  GRAY_500: "#a0aec0",
  GRAY_600: "#718096",
  GRAY_800: "#2d3748",
  WHITE: "#ffffff",
  GRADIENT_BLUE: "linear-gradient(135deg, #3182ce 0%, #20274a 100%)",
} as const;

export const PageContainer = styled(Box)(() => ({
  minHeight: "100vh",
  padding: "2rem 1.5rem",
  background: `linear-gradient(180deg, ${TOKENS.GRAY_100} 0%, ${TOKENS.GRAY_200} 100%)`,
  "@media (max-width: 1024px)": { padding: "1.25rem 1rem" },
  "@media (max-width: 600px)": { padding: "0.75rem 0.5rem 5rem" },
}));

export const PageTitle = styled(Typography)<{ component?: ElementType }>(() => ({
  color: TOKENS.PRIMARY_BLUE,
  fontWeight: 700,
  fontSize: "1.75rem",
  letterSpacing: "-0.02em",
  marginBottom: "0.25rem",
  "@media (max-width: 600px)": { fontSize: "1.35rem", marginBottom: 0 },
}));

export const PageSubtitle = styled(Typography)(() => ({
  color: TOKENS.ACCENT_BLUE,
  fontWeight: 700,
  fontSize: "0.75rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  marginBottom: "1.25rem",
}));

export const SectionCard = styled(Box)(() => ({
  background: TOKENS.WHITE,
  borderRadius: 12,
  padding: "1.5rem",
  marginBottom: "1.25rem",
  boxShadow:
    "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
  "@media (max-width: 1024px)": { padding: "1.25rem", marginBottom: "1rem" },
  "@media (max-width: 600px)": {
    padding: "0.875rem",
    borderRadius: 10,
    marginBottom: "0.75rem",
  },
}));

export const SectionTitle = styled(Typography)<{ component?: ElementType }>(() => ({
  color: TOKENS.PRIMARY_BLUE,
  fontWeight: 700,
  fontSize: "1.1rem",
  letterSpacing: 0,
  marginBottom: "0.75rem",
}));

export const SectionLead = styled(Typography)(() => ({
  color: TOKENS.LIGHT_BLUE,
  fontSize: "0.875rem",
  lineHeight: 1.5,
  marginBottom: "1rem",
}));

export const PrimaryButton = styled(Button)(() => ({
  background: TOKENS.GRADIENT_BLUE,
  color: TOKENS.WHITE,
  borderRadius: 12,
  padding: "12px 28px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.95rem",
  letterSpacing: 0.1,
  boxShadow: "0 4px 14px rgba(49,130,206,0.25)",
  "@media (max-width: 600px)": {
    padding: "10px 18px",
    fontSize: "0.9rem",
    width: "auto",
    minWidth: 140,
  },
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 50%)",
    opacity: 0,
    transition: "opacity 0.25s",
  },
  "&:hover": {
    background: TOKENS.GRADIENT_BLUE,
    transform: "translateY(-1px)",
    boxShadow: "0 8px 22px rgba(49,130,206,0.4)",
    "&::before": { opacity: 1 },
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(49,130,206,0.25)",
  },
  "&:disabled": {
    background: TOKENS.GRAY_300,
    color: TOKENS.GRAY_500,
    boxShadow: "none",
  },
}));

export const SecondaryButton = styled(Button)(() => ({
  color: TOKENS.LIGHT_BLUE,
  borderRadius: 10,
  padding: "10px 22px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.95rem",
  "&:hover": { background: TOKENS.GRAY_100 },
}));

export const TotalsBar = styled(Box)(() => ({
  background: TOKENS.WHITE,
  borderRadius: 12,
  padding: "1.25rem 1.5rem",
  border: `1px solid ${TOKENS.GRAY_200}`,
  boxShadow:
    "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
  marginBottom: "1.25rem",
  position: "relative",
  overflow: "hidden",
  "@media (max-width: 600px)": {
    padding: "0.875rem 1rem",
    borderRadius: 10,
    marginBottom: "0.875rem",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `linear-gradient(135deg, ${TOKENS.GRAY_100} 0%, ${TOKENS.WHITE} 50%, ${TOKENS.GRAY_100} 100%)`,
    opacity: 0.5,
    pointerEvents: "none",
  },
}));
